/**
 * ExchangeCard — a catchball exchange rendered consumer-grade on the C Mode
 * session dashboard (Doc 26; SPEC-01 §4.1).
 *
 * One server-authoritative ResponsibilityCard → one card:
 *   REQUIRED → "The ball is with you" + exactly one CTA (valid_actions[0])
 *   WAITING  → named waiting state, no CTA (the ball is with someone else)
 *
 * Classification is never recomputed here (Doc 06). NONE / invalid cards are
 * filtered out upstream — absent, never disabled (Doc 26 rule 2).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ResponsibilityCard } from '../../../types/prioritization/responsibility';
import { getResponsibilityNavigationHref } from '../../../lib/responsibilityLinks';
import { C } from '../theme/participantTokens';
import { deadlinePhrase, SCOPE_PLAIN_LABELS } from '../utils/exchangeCards';

interface ExchangeCardProps {
  card: ResponsibilityCard;
}

export function ExchangeCard({ card }: ExchangeCardProps) {
  const navigate = useNavigate();
  const [navError, setNavError] = useState(false);

  const required = card.classification === 'REQUIRED';
  const href = getResponsibilityNavigationHref(card);
  const ctaLabel = card.valid_actions[0]?.label ?? 'Open';
  const due = deadlinePhrase(card.deadline);
  const accent = required ? C.coral : C.slate;

  const open = () => {
    if (!href) {
      setNavError(true);
      return;
    }
    navigate(href);
  };

  return (
    <div
      className="pp-anim pp-session-card"
      onClick={required ? open : undefined}
      style={{
        background: C.surface,
        border: `1px solid ${C.rule}`,
        borderLeft: `2px solid ${accent}`,
        borderRadius: 4,
        padding: '14px 16px',
        cursor: required ? 'pointer' : 'default',
        opacity: required ? 1 : 0.8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'JetBrains Mono', fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: C.textDim, marginBottom: 5, flexWrap: 'wrap' }}>
        <span style={{ color: 'rgba(212,160,23,0.75)' }}>
          {SCOPE_PLAIN_LABELS[card.scope] ?? card.scope}
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: required ? C.coral : C.textTer }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: required ? C.coral : C.slate, flexShrink: 0 }} />
          {required ? 'The ball is with you' : 'With them'}
        </span>
        {due && (
          <>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ color: due === 'Overdue' ? C.coral : C.textDim }}>{due}</span>
          </>
        )}
      </div>

      <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1rem', fontWeight: 400, color: C.textPri, lineHeight: 1.3 }}>
        {card.subject}
      </div>

      <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.82rem', fontWeight: 300, color: C.textTer, marginTop: 6, lineHeight: 1.4 }}>
        {card.why}
        {!required && ' Nothing is needed from you right now.'}
      </div>

      {navError && (
        <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.78rem', color: C.coral, marginTop: 8 }}>
          This exchange has no open page yet — it may have just resolved. Refresh to update your cards.
        </div>
      )}

      {required && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 10, marginTop: 10, borderTop: `1px solid ${C.rule}` }}>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); open(); }}
            style={{
              height: 28,
              padding: '0 12px',
              background: 'none',
              border: '1px solid rgba(192,83,58,0.4)',
              borderRadius: 3,
              cursor: 'pointer',
              fontFamily: 'JetBrains Mono',
              fontSize: '0.58rem',
              letterSpacing: '0.1em',
              color: C.coral,
            }}
          >
            {ctaLabel} →
          </button>
        </div>
      )}
    </div>
  );
}
